import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { POSTHOG_FEATURE_FLAGS, usePostHogFeatureFlagDefaultOn } from 'scalarAnalytics/postHogFeatureFlags';
import { colours, hslToHex, intensity, navGradient, SHOW } from './season';
import type { HalloweenLevel } from './season';

export interface HalloweenState {
  accent: string;
  accentHex: string;
  days: number;
  enabled: boolean;
  level: HalloweenLevel;
  navGradient: string;
  paper: string;
  showPumpkin: boolean;
  still: boolean;
  t: number;
}

const INACTIVE_HALLOWEEN: HalloweenState = {
  accent: '',
  accentHex: '',
  days: 99,
  enabled: false,
  level: 'off',
  navGradient: '',
  paper: '',
  showPumpkin: false,
  still: false,
  t: 0,
};

const LEVELS: readonly HalloweenLevel[] = ['off', 'subtle', 'full'];

// QA: ?halloween=0..14 overrides days-to-go. ?halloweenLevel=off|subtle|full overrides Subtle.
const readPreviewDays = (search: string): number | null => {
  const value = new URLSearchParams(search).get('halloween');
  if (value == null || value === '') {
    return null;
  }
  const days = Number(value);
  if (!Number.isInteger(days) || days < 0 || days > 14) {
    return null;
  }
  return days;
};

const readLevel = (search: string): HalloweenLevel | null => {
  const value = new URLSearchParams(search).get('halloweenLevel');
  if (value === 'off' || value === 'subtle' || value === 'full') {
    return value;
  }
  return null;
};

const WorkboardHalloweenContext = createContext<HalloweenState>(INACTIVE_HALLOWEEN);

export const WorkboardHalloweenProvider = ({ value, children }: { value: HalloweenState; children: ReactNode }) => (
  <WorkboardHalloweenContext.Provider value={value}>{children}</WorkboardHalloweenContext.Provider>
);

export const useWorkboardHalloweenState = (): HalloweenState => useContext(WorkboardHalloweenContext);

export const useWorkboardHalloween = (surfaceEnabled: boolean): HalloweenState => {
  const flagOn = usePostHogFeatureFlagDefaultOn(POSTHOG_FEATURE_FLAGS.WORKBOARD_HALLOWEEN);
  const location = useLocation();
  const [now, setNow] = useState(() => new Date());
  const [still, setStill] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  );

  useEffect(() => {
    const refresh = () => setNow(new Date());
    const intervalId = window.setInterval(refresh, 60 * 60 * 1000);
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        refresh();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const apply = () => setStill(media.matches);
    apply();
    media.addEventListener('change', apply);
    return () => media.removeEventListener('change', apply);
  }, []);

  const state = useMemo(() => {
    if (!surfaceEnabled || !flagOn) {
      return INACTIVE_HALLOWEEN;
    }
    const level = readLevel(location.search) ?? 'subtle';
    if (!LEVELS.includes(level) || level === 'off') {
      return INACTIVE_HALLOWEEN;
    }
    const season = intensity(now, readPreviewDays(location.search));
    if (!season.active) {
      return INACTIVE_HALLOWEEN;
    }
    const palette = colours(season.t, level);
    return {
      accent: palette.accent,
      accentHex: hslToHex(palette.accent),
      days: season.days,
      enabled: true,
      level,
      navGradient: navGradient(season.t, level),
      paper: palette.paper,
      showPumpkin: season.days <= SHOW.pumpkin,
      still,
      t: season.t,
    };
  }, [flagOn, location.search, now, still, surfaceEnabled]);

  useEffect(() => {
    const root = document.documentElement;
    if (!state.enabled) {
      root.removeAttribute('data-wbh');
      root.removeAttribute('data-wbh-still');
      root.style.removeProperty('--wbh-accent');
      root.style.removeProperty('--wbh-paper');
      return undefined;
    }
    root.setAttribute('data-wbh', state.level);
    root.toggleAttribute('data-wbh-still', state.still);
    root.style.setProperty('--wbh-accent', state.accent);
    root.style.setProperty('--wbh-paper', state.paper);
    return () => {
      root.removeAttribute('data-wbh');
      root.removeAttribute('data-wbh-still');
      root.removeAttribute('data-wbh-shiver');
      root.removeAttribute('data-wbh-glow');
      root.style.removeProperty('--wbh-accent');
      root.style.removeProperty('--wbh-paper');
    };
  }, [state.accent, state.enabled, state.level, state.paper, state.still]);

  return state;
};
